import type { Dialog as BaseDialog } from '@base-ui/react';

import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ForumAuthor as TForumAuthor } from '~/api/forum/types';

import { Avatar, Dialog, Logo } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { useRerender } from '~/hooks/useRerender';
import { clsx } from '~/styles/utils';
import { selectBestRelativeTimeFormat } from '~/utils/dates';

import {
  authorStyle,
  contentStyle,
  createdAtStyle,
  creativeShelterBrandStyle,
  creativeShelterLogoStyle,
  dividerStyle,
  profilePictureStyle,
  textLinesStyle,
  triggerStyle,
} from './ForumAuthor.css';

type Props = {
  author: TForumAuthor;
  className?: string;
  createdAt: string;
  /** Opens the author's profile dialog, shared by all authors on the page. */
  dialogHandle: BaseDialog.Handle<TForumAuthor>;
  editedAt: string | null;
};

export const ForumAuthor = ({ author, className, createdAt, dialogHandle, editedAt }: Props) => {
  const { t } = useTranslation(['forum']);

  return (
    <Dialog.Trigger className={clsx(contentStyle, triggerStyle, className)} handle={dialogHandle} payload={author}>
      <Avatar
        aria-hidden
        className={profilePictureStyle}
        fallback={author.firstName.charAt(0)}
        shape="circle"
        src={author.profilePictureUrl ?? undefined}
      />

      <div className={textLinesStyle}>
        <p className={authorStyle}>
          <span>{author.firstName}</span>

          {author.brandName && <span className={dividerStyle}>&#x2022;</span>}

          {author.brandName && (
            <span className={clsx(author.isModerator && creativeShelterBrandStyle)}>
              {author.isModerator && <Logo className={creativeShelterLogoStyle} variant="symbol" />}

              {author.brandName}
            </span>
          )}
        </p>

        <p className={createdAtStyle}>
          <RelativeTime timestamp={createdAt} />

          {editedAt !== null && (
            <>
              <span className={dividerStyle}>&#x2022;</span>

              <span>{t('forum:post.card.edited')}</span>
            </>
          )}
        </p>
      </div>
    </Dialog.Trigger>
  );
};

const RelativeTime = ({ timestamp }: { timestamp: string }) => {
  const isPostedRecently = dayjs(timestamp).isAfter(dayjs().subtract(1, 'day'));

  const { formatDate, formatRelativeTime } = useIntl();
  const rerender = useRerender();

  // Update the relative time every minute
  useEffect(() => {
    if (!isPostedRecently) return;

    const intervalId = setInterval(rerender, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [isPostedRecently, rerender]);

  return (
    <span>
      {isPostedRecently
        ? formatRelativeTime(...selectBestRelativeTimeFormat(timestamp, { granularity: 'datetime', nowThreshold: 60 }))
        : formatDate(timestamp)}
    </span>
  );
};
